import { Dispatch } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { getPeriod } from 'dashboard/lib/utils/charting';
import { formatDateRangeEnd, formatDateRangeStart } from 'dashboard/lib/utils/formatters';
import { Environment } from 'dashboard/models/environment';
import {
  ExporterFileType,
  ExporterMetricsVerificationChecks,
  ExporterMetricsVerificationVolume,
} from 'dashboard/models/exporter';
import { MetricsVerificationChecksOverTimeResponse } from 'dashboard/models/metrics';
import { MetricsGroupBy } from 'dashboard/models/metrics-verification';
import {
  FilterRulesForType,
  setMetricsVerificationChecksOverTime,
  setVerificationMetricsFilteredVolumeSummary,
  setVerificationVolumeOverTime,
  VerificationGranularitiesByType,
  VerificationMetricsFilterParams,
  VerificationTemplatesGroupedByType,
} from 'dashboard/redux/slices/metrics-verification';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import { ApiEndpoint, ApiModel, VerificationType } from 'lib/interfaces/api';

export type FilterRulesV2 = Partial<
  Record<
    VerificationType,
    {
      or: FilterRule[][];
      verification_template_ids: string[];
      include_legacy_verifications: boolean;
    }
  >
>;

export function loadMetricsVerificationFilteredVolumeSummary({
  currentEnvironment,
  filters,
  filterRules,
  verificationTemplates,
}: {
  currentEnvironment: Environment;
  filters: VerificationMetricsFilterParams;
  // TODO: [jonny] Refactor the Format of the FilterRules to something more
  // JSON-y and API form friendly
  filterRules: Record<VerificationType, FilterRulesForType>;
  verificationTemplates: VerificationTemplatesGroupedByType;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateIds, txnTypeIds, verificationTypes, groupBy } =
      filters;
    const filteredVolumeSummary = (
      await SessionClientDashboard.getResourceMetaTyped(
        ApiEndpoint.MetricsVerificationsFilteredVolume,
        {
          filter: {
            'environment-id': currentEnvironment.id,
            'template-ids': templateIds,
            'txn-type-ids': txnTypeIds,
            'include-no-txn': txnTypeIds == null || txnTypeIds.length === 0,
            'include-no-template': templateIds.length === 0,
            'created-at-start': formatDateRangeStart(createdAtStart),
            'created-at-end': formatDateRangeEnd(createdAtEnd),
            period: getPeriod(createdAtStart, createdAtEnd),
            // Its very important to stringify this so that we do not have an issue with qs.Stringify
            // We cannot support a nested array with qs.Stringify. We'll want to pop these out in future iterations
            // This is done on a get. It may be wise to, in the future, add this to a POST and
            // attach the proper JSON elements
            'verification-filters': JSON.stringify(
              transformFilterRulesToRequest(filterRules, verificationTemplates),
            ),
            'verification-types': verificationTypes,
            'group-by': groupBy,
          },
        },
      )
    ).meta;

    dispatch(
      setVerificationMetricsFilteredVolumeSummary({
        filteredVolumeSummary,
        filterRules,
        queriedAt: new Date().toISOString(),
      }),
    );
  };
}

export function loadMetricsVerificationFilteredChecksOverTime({
  currentEnvironment,
  filters,
  filterRules,
  verificationTemplates,
  verificationGranularities,
}: {
  currentEnvironment: Environment;
  filters: VerificationMetricsFilterParams;
  filterRules: Record<VerificationType, FilterRulesForType>;
  verificationTemplates: VerificationTemplatesGroupedByType;
  verificationGranularities: VerificationGranularitiesByType;
}) {
  return async (dispatch: Dispatch) => {
    const {
      createdAtStart,
      createdAtEnd,
      templateIds,
      checkRequirements,
      verificationTypes,
      groupBy,
    } = filters;

    const verificationChecksOverTime =
      await SessionClientDashboard.getEndpoint<MetricsVerificationChecksOverTimeResponse>(
        ApiEndpoint.MetricsVerificationsFilteredChecksOverTime,
        {
          filter: {
            'environment-id': currentEnvironment.id,
            'template-ids': templateIds,
            'created-at-start': formatDateRangeStart(createdAtStart),
            'created-at-end': formatDateRangeEnd(createdAtEnd),
            'verification-check-required': checkRequirements,
            'group-by': groupBy,
            'verification-types': verificationTypes,
            // Its very important to stringify this so that we do not have an issue with qs.Stringify
            // This is done on a get. It may be wise to, in the future, add this to a POST and
            // attach the proper JSON elements
            'verification-filters': JSON.stringify(
              transformFilterRulesToRequest(filterRules, verificationTemplates),
            ),
            period: getPeriod(createdAtStart, createdAtEnd),
            granularities: JSON.stringify(verificationGranularities),
          },
        },
      );

    dispatch(
      setMetricsVerificationChecksOverTime({
        verificationChecksOverTime: verificationChecksOverTime.meta,
        queriedAt: new Date().toISOString(),
        updatedAt: verificationChecksOverTime.updated_at,
      }),
    );
  };
}

export function loadMetricsVerificationVolumeIntervals({
  currentEnvironment,
  filters,
  filterRules,
  verificationTemplates,
  verificationGranularities,
}: {
  currentEnvironment: Environment;
  filters: VerificationMetricsFilterParams;
  filterRules: Record<VerificationType, FilterRulesForType>;
  verificationTemplates: VerificationTemplatesGroupedByType;
  verificationGranularities: VerificationGranularitiesByType;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateIds, txnTypeIds, groupBy, groupByDimension } =
      filters;
    const verificationVolumeOverTime = (
      await SessionClientDashboard.getResourceMetaTyped(
        ApiEndpoint.MetricsVerificationsVolumeOverTime,
        {
          filter: {
            'environment-id': currentEnvironment.id,
            'template-ids': templateIds,
            'txn-type-ids': txnTypeIds,
            'include-no-txn': txnTypeIds == null || txnTypeIds.length === 0,
            'include-no-template': templateIds.length === 0,
            'group-by': groupBy,
            'group-by-dimensions': groupByDimension,
            'created-at-start': formatDateRangeStart(createdAtStart),
            'created-at-end': formatDateRangeEnd(createdAtEnd),
            // Its very important to stringify this so that we do not have an issue with qs.Stringify
            // This is done on a get. It may be wise to, in the future, add this to a POST and
            // attach the proper JSON elements
            'verification-filters': JSON.stringify(
              transformFilterRulesToRequest(filterRules, verificationTemplates),
            ),
            period: getPeriod(createdAtStart, createdAtEnd),
            granularities: JSON.stringify(verificationGranularities),
          },
        },
      )
    ).meta;

    dispatch(
      setVerificationVolumeOverTime({
        verificationVolumeOverTime,
        queriedAt: new Date().toISOString(),
      }),
    );
  };
}

export const loadExporterMetricsVerificationChecks =
  ({
    currentEnvironment,
    filters,
    filterRules,
    sortParams,
    fileType,
  }: {
    currentEnvironment: Environment;
    filters: VerificationMetricsFilterParams;
    filterRules: Record<VerificationType, FilterRulesForType>;
    sortParams: SortParams;
    fileType: ExporterFileType;
  }) =>
  async () => {
    const {
      createdAtStart,
      createdAtEnd,
      templateIds,
      groupBy,
      verificationTypes,
      checkRequirements,
    } = filters;
    const { sort, shouldSortDescending } = sortParams;
    const params = {
      'file-type': fileType,
      filter: {
        'environment-id': currentEnvironment.id,
        'template-ids': templateIds,
        'created-at-start': formatDateRangeStart(createdAtStart),
        'created-at-end': formatDateRangeEnd(createdAtEnd),
        'verification-check-required': checkRequirements,
        'group-by': groupBy,
        'verification-types': verificationTypes,
        // Its very important to stringify this so that we do not have an issue with qs.Stringify
        // This is done on a get. It may be wise to, in the future, add this to a POST and
        // attach the proper JSON elements
        'verification-filters': JSON.stringify(transformFilterRulesToRequest(filterRules)),
      },
      sort: `${shouldSortDescending ? '-' : ''}${sort}`,
    };

    await SessionClientDashboard.postResourceCreate<ExporterMetricsVerificationChecks>({
      type: ApiModel.ExporterMetricsVerificationChecks,
      attributes: {
        ...params,
      },
    });
  };

type VolumeExporterParams = {
  'file-type': ExporterFileType;
  filter: {
    'environment-id': string;
    'template-ids': string[];
    'txn-type-ids': string[];
    'include-no-txn': boolean;
    'include-no-template': boolean;
    'created-at-start': string;
    'created-at-end': string;
    'group-by': MetricsGroupBy | undefined;
    'verification-types': string[];
    'verification-filters': string;
  };
  sort: string;
};

export const loadExporterMetricsVerificationVolume =
  ({
    currentEnvironment,
    filters,
    filterRules,
    sortParams,
    fileType,
  }: {
    currentEnvironment: Environment;
    filters: VerificationMetricsFilterParams;
    filterRules: Record<VerificationType, FilterRulesForType>;
    sortParams: SortParams;
    fileType: ExporterFileType;
  }) =>
  async () => {
    const { createdAtStart, createdAtEnd, templateIds, txnTypeIds, groupBy, verificationTypes } =
      filters;
    const { sort, shouldSortDescending } = sortParams;
    const params: VolumeExporterParams = {
      'file-type': fileType,
      filter: {
        'environment-id': currentEnvironment.id,
        'template-ids': templateIds,
        'txn-type-ids': txnTypeIds,
        'include-no-txn': txnTypeIds == null || txnTypeIds.length === 0,
        'include-no-template': templateIds.length === 0,
        'group-by': groupBy,
        'created-at-start': formatDateRangeStart(createdAtStart),
        'created-at-end': formatDateRangeEnd(createdAtEnd),
        'verification-types': verificationTypes,
        // Its very important to stringify this so that we do not have an issue with qs.Stringify
        // This is done on a get. It may be wise to, in the future, add this to a POST and
        // attach the proper JSON elements
        'verification-filters': JSON.stringify(transformFilterRulesToRequest(filterRules)),
      },
      sort: `${shouldSortDescending ? '-' : ''}${sort}`,
    };
    await SessionClientDashboard.postResourceCreate<ExporterMetricsVerificationVolume>({
      type: ApiModel.ExporterMetricsVerificationVolume,
      // TODO: Update some of these types, this is terrible
      attributes: params as Record<string, any>,
    });
  };

export const transformFilterRulesToRequest = (
  filterRules: Record<VerificationType, FilterRulesForType>,
  verificationTemplates: VerificationTemplatesGroupedByType = {},
): FilterRulesV2 => {
  const filterRulesV2: FilterRulesV2 = {};
  for (const key in filterRules) {
    // if all templates are selected, we don't need to send the list of filtered verification templates to the server.
    // That is to say, empty is all. This is to avoid maxing out message size limit. This is usually only the case for internal users
    const allTemplateIds = verificationTemplates[key as VerificationType]?.map(({ id }) => id);
    const filteredTemplates = filterRules[key as VerificationType].verificationTemplateIds;
    const verificationTemplatesForRequest = isEqual(allTemplateIds, filteredTemplates)
      ? []
      : filteredTemplates;

    const includeLegacyVerifications =
      filterRules[key as VerificationType].includeLegacyVerifications;

    filterRulesV2[key as VerificationType] = {
      or: filterRules[key as VerificationType].or,
      verification_template_ids: verificationTemplatesForRequest,
      include_legacy_verifications: includeLegacyVerifications,
    };
  }
  return filterRulesV2;
};
